import { ApiError, authHeaders } from '@/services/authApi';
import type { PoolLeadSummary } from '@/services/leadsApi';
import type { DetailLead } from '@/types/detailLead';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export interface FavoriteItem {
  id: number;
  leadId: string;
  leadTitle: string;
  companyName: string;
  createdAt: string;
  lead: PoolLeadSummary | null;
}

export interface FavoriteList {
  items: FavoriteItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface RelatedLead {
  id: string;
  title: string;
  name: string;
  priority: string;
  leadType: string;
  companyName: string;
}

export interface TargetClientItem {
  id: number;
  companyId: string;
  companyName: string;
  sourceLeadId: string | null;
  sourceLeadTitle: string;
  createdAt: string;
  relatedLeads: RelatedLead[];
}

async function readApiError(res: Response, fallback: string): Promise<ApiError> {
  const body = await res.json().catch(() => ({}));
  const message = body?.error?.message || fallback;
  const code = body?.error?.code || 'UNKNOWN';
  return new ApiError(message, code, res.status);
}

export async function listFavorites(page = 1, pageSize = 100): Promise<FavoriteList> {
  const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  const res = await fetch(`${API_BASE_URL}/api/v1/me/favorites?${query}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await readApiError(res, '加载收藏失败');
  const json = await res.json();
  return json.data as FavoriteList;
}

export async function addFavorite(leadId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/me/favorites`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ leadId }),
  });
  if (!res.ok) throw await readApiError(res, '收藏失败');
}

export async function removeFavorite(leadId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/me/favorites/${encodeURIComponent(leadId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw await readApiError(res, '取消收藏失败');
}

export async function listClients(keyword = ''): Promise<{ items: TargetClientItem[] }> {
  const query = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
  const res = await fetch(`${API_BASE_URL}/api/v1/me/clients${query}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await readApiError(res, '加载目标客户失败');
  const json = await res.json();
  return json.data as { items: TargetClientItem[] };
}

export async function addTargetClient(input: {
  companyId: string;
  companyName: string;
  sourceLeadId?: string;
}): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/me/clients`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await readApiError(res, '加入目标客户失败');
}

export async function removeTargetClient(companyId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/me/clients/${encodeURIComponent(companyId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw await readApiError(res, '移除目标客户失败');
}

export async function exportLeadRows(leadIds: string[]): Promise<DetailLead[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/leads/export`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ leadIds }),
  });
  if (!res.ok) throw await readApiError(res, '导出失败');
  const json = await res.json();
  return (json.data.items ?? []) as DetailLead[];
}
